import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import Loading from './components/Loading';
import useAdminData, { responseError } from './useAdminData';
import { adminFetch, AdminAuthError } from './adminAuth';
import { formatDateTime, formatMoney } from './format';
import { SALE_DATE_FIELDS } from './saleDates';

const STATUS_VARIANTS = { open: 'success', closed: 'danger', maintenance: 'warning' };

const DATE_ROWS = SALE_DATE_FIELDS.map(field => [field.label, field.name]);

function Stat({ label, value, detail, to }) {
  return (
    <Col xs={12} sm={6} lg={3} className="mb-3">
      <Card className="h-100">
        <Card.Body>
          <Card.Subtitle className="text-muted mb-2">{label}</Card.Subtitle>
          <Card.Title as="div" className="h3 mb-1">{value}</Card.Title>
          {detail && <Card.Text className="small text-muted mb-0">{detail}</Card.Text>}
          {to && <Link to={to} className="small stretched-link">View</Link>}
        </Card.Body>
      </Card>
    </Col>
  );
}

const DROP_OFF_FILES = [
  { file: 'consigners', filename: 'consigner_no_shows.csv', label: 'No-show consigners' },
  { file: 'items', filename: 'items_not_brought.csv', label: 'Items not brought' }
];

/** Saves a drop-off CSV (the files the reporting project's payout sheets read) */
export async function downloadDropOffFile({ file, filename }) {
  const response = await adminFetch(`/admin/reports/drop-off?file=${file}`);
  if (!response.ok) {
    throw new Error(await responseError(response));
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function DropOffCard({ notDroppedOffCount, noShowCount }) {
  const history = useHistory();
  const [error, setError] = useState(null);

  const download = async entry => {
    setError(null);
    try {
      await downloadDropOffFile(entry);
    } catch (err) {
      if (err instanceof AdminAuthError) {
        history.replace('/admin/login');
        return;
      }
      setError(err.message);
    }
  };

  return (
    <Card className="mb-3">
      <Card.Header>Drop-off</Card.Header>
      <Card.Body>
        <p>
          <strong>{notDroppedOffCount}</strong> item{notDroppedOffCount === 1 ? '' : 's'} not dropped off,
          including every item of <strong>{noShowCount}</strong> no-show consigner{noShowCount === 1 ? '' : 's'}.
          Mark them on each consigner's or item's page.
        </p>
        <p className="small text-muted">
          The no-show file also lists consigners who never added an item, as the reporting scripts' template did.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="small text-muted mb-2">
          For the payout sheets, save these into the reporting project's <code>not_consigned</code> folder:
        </p>
        {DROP_OFF_FILES.map(entry => (
          <Button key={entry.file} variant="outline-secondary" size="sm" className="mr-2 mb-2" onClick={() => download(entry)}>
            Download {entry.filename}
          </Button>
        ))}
      </Card.Body>
    </Card>
  );
}

export default function AdminDashboard() {
  const { data, error, loading, waking, reload } = useAdminData('/admin/dashboard/summary');

  if (loading && !data) {
    return <Loading waking={waking} />;
  }
  if (error) {
    return <Alert variant="danger">{error} <Button variant="link" onClick={reload}>Try again</Button></Alert>;
  }

  const { consignmentStatus, dates } = data;
  const statusUnknown = consignmentStatus === 'unknown';
  const missingDates = DATE_ROWS.filter(([, key]) => !dates[key]).map(([label]) => label);

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <h2 className="mb-0 mr-3">Dashboard</h2>
        <Badge variant={STATUS_VARIANTS[consignmentStatus] || 'secondary'} className="p-2">
          {statusUnknown ? 'Consignment status unknown' : `Consignment ${consignmentStatus}`}
        </Badge>
        <Button variant="outline-secondary" size="sm" className="ml-auto" onClick={reload} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {statusUnknown && (
        <Alert variant="danger">
          The consignment status setting can't be read. Consigners are let in as if the sale were
          open until it is fixed. Check the <code>consignment-status</code> parameter (see the README).
        </Alert>
      )}
      {dates.isFallback && (
        <Alert variant="danger">
          The sale date setting can't be read, so item numbers may use the wrong year. Check the
          <code> date-configuration</code> parameter (see the README) before consigners add items.
        </Alert>
      )}
      {!dates.isFallback && missingDates.length > 0 && (
        <Alert variant="warning">Not set yet: {missingDates.join(', ')}.</Alert>
      )}

      <Row>
        <Stat
          label="Consigners"
          value={data.consignerCount}
          to="/admin/consigners"
        />
        <Stat
          label="Consigners with no items"
          value={data.emptyConsignerCount}
          detail="Signed up but haven't added anything"
          to="/admin/consigners?show=empty"
        />
        <Stat
          label="Items"
          value={data.itemCount}
          to="/admin/items"
          detail={`${data.discountEligibleCount} discount eligible` +
            (data.unitCount !== data.itemCount ? ` · ${data.unitCount} counting quantities` : '')}
        />
        <Stat label="Total asking value" value={formatMoney(data.totalValue)} detail="Price × quantity" />
      </Row>

      <DropOffCard notDroppedOffCount={data.notDroppedOffCount} noShowCount={data.noShowCount} />

      <Card>
        <Card.Header>{dates.currentYear} sale dates (Eastern time)</Card.Header>
        <Table size="sm" className="mb-0">
          <tbody>
            {DATE_ROWS.map(([label, key]) => (
              <tr key={key}>
                <th scope="row" className="font-weight-normal text-muted" style={{ width: '40%' }}>{label}</th>
                <td>{formatDateTime(dates[key])}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
